import { Team } from './tile-models';
import { DisplayText } from '@kikkz/shared/i18n/util';

export interface Competition {
  name: string;
  imageUrl: string;
}

/**
 * Competitions the ranking can be filtered by — matches the backend `competition` query
 * enum. Single source of truth for {@link RankingCompetition} and
 * {@link isRankingCompetition}.
 */
export const RANKING_COMPETITIONS = ['BUNDESLIGA', 'DFB_POKAL', 'UEFA_CHAMPIONS_LEAGUE', 'UEFA_EURO_LEAGUE'] as const;

/**
 * Competition the ranking can be filtered by — matches the backend `competition` query enum.
 * `null` means no filter (the whole "Saison" / overall ranking).
 */
export type RankingCompetition = (typeof RANKING_COMPETITIONS)[number];

/**
 * Whether the backend value is a competition the ranking filter knows. The backend enum
 * can grow ahead of the frontend, so wire values go through this guard rather than a
 * blind `as RankingCompetition` cast.
 */
export function isRankingCompetition(value: string | null | undefined): value is RankingCompetition {
  return !!value && (RANKING_COMPETITIONS as readonly string[]).includes(value);
}

export interface RankingEntry {
  /** Backend user id (UUID) — needed for the user-stats lookup. Optional: not every tenant/mock provides it. */
  id?: string;
  /** null = not ranked yet (e.g. backend sent userRanking: null) — rendered as a dash. */
  rank: number | null;
  /** Rank at the previous update; null when unknown / newly listed. Drives the up/down indicator. */
  previousRank: number | null;
  name: string;
  /** Whether this user is a member — shown as the "+" icon next to the name. */
  isMember: boolean;
  points: number;
  results: number;
  goalScorers: number;
  lineups: number;
  bonusQuestions: number;
  isCurrentUser: boolean;
}

// No pagination: the ranking is loaded in full ("alles oder nichts").
export interface RankingPage {
  entries: RankingEntry[];
  currentUserEntry: RankingEntry | null;
}

/** Which sub-question of a fixture a detail card belongs to. */
export type MatchdayPredictionKind = 'RESULT' | 'GOAL_SCORER' | 'LINEUP' | 'ADDITIONAL_QUESTION';

/** What the tipp value expresses — decides the label of the "your prediction" row. */
export type MatchdayTippMeaning = 'PREDICTION' | 'HIT_COUNT' | 'PICK_COUNT';

/**
 * One sub-question of a fixture as shown in the user-stats modal.
 *
 * Carries no translation keys on purpose: which label a `kind` / `tippMeaning`
 * gets is a presentation decision and belongs to the rendering component.
 */
export interface MatchdayPredictionDetail {
  kind: MatchdayPredictionKind;
  points: number;
  tippMeaning: MatchdayTippMeaning;
  tippValue: DisplayText;
  /** `null` hides the "actual outcome" row. */
  actualValue: DisplayText | null;
}

export interface MatchdayPoints {
  fixtureId: string;
  /** ISO kick-off timestamp — the modal formats weekday/time with Angular's DatePipe. */
  date: string;
  team: Team;
  opponent: Team;
  /** Whether `team` played at home — drives the home-left / away-right icon order in the stats modal. */
  teamIsHome: boolean;
  competition: Competition;
  points: number | null;
  maxPoints: number;
  played: boolean;
  /** Per-question sub-scores — `null` when the backend has no score for the fixture (rendered as "-"). */
  results: number | null;
  goalScorers: number | null;
  lineups: number | null;
  bonusQuestions: number | null;
  predictionDetails: MatchdayPredictionDetail[];
}

export interface RankingUserDetail {
  name: string;
  /** null = not ranked yet (e.g. backend sent no currentUserRanking) — rendered as a dash. */
  totalPoints: number | null;
  /** null = not ranked yet (e.g. backend sent no currentUserRanking) — rendered as a dash. */
  totalRank: number | null;
  matchdays: MatchdayPoints[];
}
